import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Card, Chip, CircularProgress, Alert, Pagination,
  IconButton, Tooltip, Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import {
  AccountBalance, Refresh, ExpandMore, Receipt as ReceiptIcon,
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import { colors } from '../../theme/theme';

const inr = (n: number) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtPeriod = (s?: string, e?: string) => {
  if (!s || !e) return '—';
  const a = new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  const b = new Date(e).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  return `${a} – ${b}`;
};
const monthKey = (d: string) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`; };
const monthLabel = (k: string) => { const [y, m] = k.split('-'); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }); };

const softBadgeSx = (color: string) => ({
  bgcolor: `${color}15`,
  color,
  fontWeight: 600,
  fontSize: '0.75rem',
  border: `1px solid ${color}40`,
  height: 'auto',
  '& .MuiChip-label': { px: 1.5, py: 0.75 },
});

const filterPillSx = (active: boolean, color: string) => ({
  flexShrink: 0,
  fontWeight: 700,
  cursor: 'pointer',
  bgcolor: active ? color : `${color}14`,
  color: active ? '#fff' : color,
  border: `1px solid ${color}40`,
  height: 'auto',
  '& .MuiChip-label': { px: 1.5, py: 0.75 },
});

const tableHeadSx = {
  fontWeight: 700,
  bgcolor: colors.backgroundTertiary,
  fontSize: 12,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5,
};

const PAGE_SIZE = 50;

const parseStatementsResponse = (res: any) => {
  const data = res?.data ?? res;
  return {
    statements: data?.statements ?? [],
    count: data?.count ?? 0,
    pages: Math.max(1, data?.pages ?? 1),
  };
};

const StatusChip: React.FC<{ s: any }> = ({ s }) => {
  const isPaid = s.status === 'paid';
  const color = isPaid ? colors.success : colors.warning;
  const label = isPaid ? 'Paid' : s.isOverdue ? 'Overdue' : 'Pending';
  return (
    <Chip
      size="small"
      label={
        <Box display="flex" alignItems="center" gap={0.75}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
          {label}
        </Box>
      }
      sx={softBadgeSx(color)}
    />
  );
};

/** Super-admin payouts history — all studios, grouped by month. */
const StudioPayoutsTab: React.FC = () => {
  const [statements, setStatements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useState<'all' | 'pending' | 'paid'>('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusCounts, setStatusCounts] = useState({ all: 0, paid: 0, pending: 0 });
  const [expanded, setExpanded] = useState<string | false>(false);
  const [receiptLoadingId, setReceiptLoadingId] = useState<string | null>(null);
  const hasAutoExpanded = useRef(false);

  const openReceipt = async (s: any) => {
    setReceiptLoadingId(s._id);
    try {
      const blob: Blob = await apiService.generateStudioInvoice(s.studio?._id, {
        startDate: s.weekStart,
        endDate: s.weekEnd,
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError('Could not open the receipt.');
    } finally {
      setReceiptLoadingId(null);
    }
  };

  const fetchStatusCounts = useCallback(async () => {
    try {
      const [allRes, paidRes, pendingRes] = await Promise.all([
        apiService.getStudioPaymentStatements({ pageSize: 1, page: 1 }),
        apiService.getStudioPaymentStatements({ pageSize: 1, page: 1, status: 'paid' }),
        apiService.getStudioPaymentStatements({ pageSize: 1, page: 1, status: 'pending' }),
      ]);
      setStatusCounts({
        all: parseStatementsResponse(allRes).count,
        paid: parseStatementsResponse(paidRes).count,
        pending: parseStatementsResponse(pendingRes).count,
      });
    } catch {
      // non-critical — pill counts fall back to loaded page totals
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getStudioPaymentStatements({
        pageSize: PAGE_SIZE,
        page,
        status: statusTab,
      });
      const parsed = parseStatementsResponse(res);
      setStatements(parsed.statements);
      setTotalCount(parsed.count);
      setTotalPages(parsed.pages);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [statusTab, page]);

  const refresh = useCallback(async () => {
    await Promise.all([load(), fetchStatusCounts()]);
  }, [load, fetchStatusCounts]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { fetchStatusCounts(); }, [fetchStatusCounts]);

  const handleStatusTabChange = (value: 'all' | 'paid' | 'pending') => {
    setStatusTab(value);
    setPage(1);
    setExpanded(false);
    hasAutoExpanded.current = false;
  };

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);

  const months = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const s of statements) {
      if (!s.weekStart) continue;
      const k = monthKey(s.weekStart);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(s);
    }
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([key, list]) => ({
        key,
        label: monthLabel(key),
        items: list.sort((a, b) => new Date(b.weekStart).getTime() - new Date(a.weekStart).getTime()),
        paidNet: list.filter(x => x.status === 'paid').reduce((t, x) => t + (x.netSettlement || 0), 0),
        pendingNet: list.filter(x => x.status !== 'paid').reduce((t, x) => t + (x.netSettlement || 0), 0),
      }));
  }, [statements]);

  useEffect(() => {
    if (months.length && !hasAutoExpanded.current) {
      setExpanded(months[0].key);
      hasAutoExpanded.current = true;
    }
  }, [months]);

  const filterPills: { value: 'all' | 'paid' | 'pending'; label: string; color: string; count: number }[] = [
    { value: 'all', label: 'All', color: colors.primary, count: statusCounts.all },
    { value: 'paid', label: 'Paid', color: colors.success, count: statusCounts.paid },
    { value: 'pending', label: 'Pending', color: colors.warning, count: statusCounts.pending },
  ];

  return (
    <Box>
      {/* Filter row */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
        mb={2}
      >
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          {filterPills.map(pill => (
            <Chip
              key={pill.value}
              label={`${pill.label} ${pill.count}`}
              onClick={() => handleStatusTabChange(pill.value)}
              size="small"
              sx={filterPillSx(statusTab === pill.value, pill.color)}
            />
          ))}
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} size="small" sx={{ ml: 0.5 }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Weekly settlement cycle · <strong>Friday → Thursday</strong>
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box p={4} textAlign="center"><CircularProgress /></Box>
        ) : months.length === 0 ? (
          <Box p={5} textAlign="center">
            <AccountBalance sx={{ fontSize: 40, color: `${colors.primary}55`, mb: 1 }} />
            <Typography color="text.secondary">No payouts in this view yet.</Typography>
          </Box>
        ) : (
          <Box p={2}>
            {months.map(m => (
              <Accordion
                key={m.key}
                expanded={expanded === m.key}
                onChange={() => setExpanded(expanded === m.key ? false : m.key)}
                disableGutters
                sx={{
                  '&:before': { display: 'none' },
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 2,
                  mb: 1.5,
                  overflow: 'hidden',
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{ px: 2, py: 0.5, bgcolor: colors.backgroundTertiary }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={1} flexWrap="wrap" gap={1}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Typography variant="body1" fontWeight={700}>{m.label}</Typography>
                      <Chip
                        label={`${m.items.length} week${m.items.length !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          bgcolor: colors.backgroundTertiary,
                          color: 'text.secondary',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          border: `1px solid ${colors.divider}`,
                          height: 'auto',
                          '& .MuiChip-label': { px: 1.25, py: 0.5 },
                        }}
                      />
                    </Box>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ color: colors.success, fontWeight: 700 }}>
                        {inr(m.paidNet)} paid
                      </Typography>
                      {m.pendingNet > 0 && (
                        <Typography variant="body2" sx={{ color: colors.warning, fontWeight: 700 }}>
                          {inr(m.pendingNet)} pending
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={tableHeadSx}>Week</TableCell>
                          <TableCell sx={tableHeadSx}>Studio</TableCell>
                          <TableCell sx={tableHeadSx} align="right">Total</TableCell>
                          <TableCell sx={tableHeadSx} align="right">Net Payout</TableCell>
                          <TableCell sx={tableHeadSx}>Status</TableCell>
                          <TableCell sx={tableHeadSx}>Paid On</TableCell>
                          <TableCell sx={tableHeadSx} align="right">Receipt</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {m.items.map(s => (
                          <TableRow key={s._id} hover sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell>
                              <Typography variant="body2">{fmtPeriod(s.weekStart, s.weekEnd)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>
                                {s.studio?.studioName || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{inr(s.totalRevenue)}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600} color={colors.success}>
                                {/* The figure actually transferred — the
                                    totals above already use it, so showing the
                                    raw net here made the column disagree with
                                    its own sum. */}
                                {inr(s.finalSettlement ?? s.netSettlement)}
                              </Typography>
                            </TableCell>
                            <TableCell><StatusChip s={s} /></TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {s.status === 'paid' ? fmtDate(s.paidAt) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={s.status === 'paid' ? 'View receipt' : 'Receipt available after payment'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => openReceipt(s)}
                                    disabled={s.status !== 'paid' || receiptLoadingId === s._id}
                                    sx={{
                                      color: s.status === 'paid' ? 'text.secondary' : 'text.disabled',
                                      border: `1px solid ${colors.divider}`,
                                      borderRadius: 1,
                                      opacity: s.status === 'paid' ? 1 : 0.45,
                                    }}
                                  >
                                    {receiptLoadingId === s._id
                                      ? <CircularProgress size={16} />
                                      : <ReceiptIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  px: 1,
                  pt: 1,
                  pb: 0.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {totalCount === 0 ? '0 statements' : `${rangeStart}–${rangeEnd} of ${totalCount}`}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, nextPage) => {
                    setPage(nextPage);
                    setExpanded(false);
                    hasAutoExpanded.current = false;
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  shape="rounded"
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default StudioPayoutsTab;
